import React, { useState, useEffect } from 'react';
import { 
  DeliveryPricingSettings,
  createDefaultDeliveryPricingSettings
} from '../../../types';
import { Button, Input } from '../../atoms';
import './DeliverySettingsForm.css';

interface DeliverySettingsFormProps {
  settings: DeliveryPricingSettings | null;
  onSubmit: (settings: Partial<DeliveryPricingSettings>) => void;
  onCancel: () => void;
  loading?: boolean;
}

const DeliverySettingsForm: React.FC<DeliverySettingsFormProps> = ({
  settings,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    defaultDeliveryPrice: 5.00,
    freeDeliveryThreshold: 50.00,
    isActive: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settings) {
      setFormData({
        defaultDeliveryPrice: settings.defaultDeliveryPrice,
        freeDeliveryThreshold: settings.freeDeliveryThreshold,
        isActive: settings.isActive
      });
    } else {
      const defaultSettings = createDefaultDeliveryPricingSettings();
      setFormData({
        defaultDeliveryPrice: defaultSettings.defaultDeliveryPrice,
        freeDeliveryThreshold: defaultSettings.freeDeliveryThreshold,
        isActive: defaultSettings.isActive
      });
    }
  }, [settings]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.defaultDeliveryPrice < 0) {
      newErrors.defaultDeliveryPrice = 'Default delivery price must be 0 or greater';
    }

    if (formData.freeDeliveryThreshold < 0) {
      newErrors.freeDeliveryThreshold = 'Free delivery threshold must be 0 or greater';
    }

    if (formData.freeDeliveryThreshold > 0 && formData.defaultDeliveryPrice > 0 && 
        formData.freeDeliveryThreshold <= formData.defaultDeliveryPrice) {
      newErrors.freeDeliveryThreshold = 'Free delivery threshold should be higher than default delivery price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const isEditing = !!settings;

  return (
    <div className="delivery-settings-form">
      <div className="form-header">
        <h3>{isEditing ? 'Edit Delivery Settings' : 'Create Delivery Settings'}</h3>
        <p>
          Configure default delivery pricing and free delivery thresholds for your business.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <Input
            id="defaultDeliveryPrice"
            name="defaultDeliveryPrice"
            label="Default Delivery Price (€)"
            type="number"
            value={formData.defaultDeliveryPrice}
            onChange={handleInputChange}
            placeholder="5.00"
            min={0}
            step={0.01}
            required
            error={errors.defaultDeliveryPrice}
            disabled={loading}
          />
          <div className="field-help">
            This price will be used for locations not assigned to any delivery tier.
          </div>
        </div>

        <div className="form-group">
          <Input
            id="freeDeliveryThreshold"
            name="freeDeliveryThreshold"
            label="Free Delivery Threshold (€)"
            type="number"
            value={formData.freeDeliveryThreshold}
            onChange={handleInputChange}
            placeholder="50.00"
            min={0}
            step={0.01}
            required
            error={errors.freeDeliveryThreshold}
            disabled={loading}
          />
          <div className="field-help">
            Orders above this amount will qualify for free delivery regardless of location.
          </div>
        </div>

        <div className="form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              disabled={loading}
            />
            <label htmlFor="isActive">Active (delivery pricing is enabled)</label>
          </div>
          <div className="field-help">
            When disabled, all delivery pricing will be turned off.
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Settings' : 'Create Settings')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DeliverySettingsForm;
